import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { PolicyFormValues } from './policy-dialog';
import { policyActions } from '../../shared/policy-actions';
import { SectionHeader } from '../../shared/section-header';
import SharedAutocomplete from '../../shared/shared-autocomplete';
import { useRef, useState } from 'react';
import AvatarChip from '../../shared/avatar-chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormHelperText from '@mui/material/FormHelperText';
interface StepOneProps {
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  setValue: UseFormSetValue<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
}

export default function StepOne({
  control,
  watch,
  setValue,
  errors,
}: StepOneProps) {
  const policyTypeOptions = [
    { displayValue: 'Standard', value: 'standard' },
    { displayValue: 'New User Created', value: 'newUser' },
  ];

  const scheduled = watch('scheduled') || false;
  const useRunDate = watch('useRunDate') || false;
  const scheduleMode = useRunDate ? 'runDate' : scheduled ? 'recurring' : '';
  const policyType = watch('policyType') || 'standard';
  const actions = watch('actions') || [];
  const actionData = watch('actionData') || [];
  const autocompleteKey = useRef(new Date().getTime().toString());
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const emailsToNotify = watch('emailsToNotify');
  const nextHour = () => dayjs().add(1, 'hour').startOf('hour');

  return (
    <Stack gap={2} width={'100%'}>
      <SectionHeader description='Please provide a policy name and choose your policy type. "New User Creation" policies are triggered on user creation. You can also determine whether this policy should be run manually or on a schedule.'>
        Basic Information
      </SectionHeader>
      <Grid container size={{ xs: 10, lg: 5 }} spacing={1}>
        <Grid size={{ xs: 12 }}>
          <Controller
            name="policyName"
            control={control}
            render={({ field }) => (
              <TextField
                required
                label="Policy Name"
                sx={{ width: '100%' }}
                {...field}
                error={!!errors.policyName}
                helperText={errors?.policyName?.message || ' '}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Controller
            name="policyType"
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'policyType',
                }}
                InputLabelProps={{
                  htmlFor: 'policyType',
                }}
                select
                label="Policy Type"
                sx={{ width: '100%' }}
                value={field.value || ''}
                onChange={(event) => {
                  const allowedActions = policyActions.map((policy) => {
                    if (policy.policyTypes.includes(event.target.value)) {
                      return policy.label;
                    }
                  });
                  const allowedActionDataTypes = policyActions.map((policy) => {
                    if (policy.policyTypes.includes(event.target.value)) {
                      return policy.value;
                    }
                  });
                  setValue(
                    'actions',
                    actions?.filter((action) => allowedActions.includes(action))
                  );
                  setValue(
                    'actionData',
                    actionData.filter((action) =>
                      allowedActionDataTypes.includes(action.type)
                    )
                  );
                  if (event.target.value === 'newUser') {
                    setValue('sourceType', 'User');
                    setValue('sources', []);
                    setValue('allSource', false);
                    setValue('targetsExcluded', []);
                    setValue('times', []);
                    setValue('dates', []);
                    setValue('daysOfWeek', false);
                    setValue('manualRun', true);
                    setValue('scheduled', false);
                    setValue('useRunDate', false);
                    setValue('runDate', 0);
                  }
                  field.onChange(event.target.value);
                }}
                helperText={' '}
              >
                {policyTypeOptions.map((option) => (
                  <MenuItem value={option.value} key={option.displayValue}>
                    {option.displayValue}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'description',
                }}
                InputLabelProps={{
                  htmlFor: 'description',
                }}
                label="Description"
                sx={{ width: '100%' }}
                helperText={' '}
              />
            )}
          />
        </Grid>
        {policyType === 'standard' ? (
          <Grid size={{ xs: 12 }}>
            <Stack sx={{ pl: 2, gap: 1 }}>
              <Controller
                name="manualRun"
                control={control}
                render={({ field: props }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...props}
                        checked={props.value}
                        onChange={(e) => {
                          // At least one of Manual Run / a schedule mode must always be
                          // active, so unchecking Manual Run is blocked while no radio
                          // option is selected.
                          if (!e.target.checked && scheduleMode === '') {
                            return;
                          }
                          props.onChange(e.target.checked);
                        }}
                      />
                    }
                    label="Manual Run"
                  />
                )}
              />
              <FormControl>
                <RadioGroup
                  value={scheduleMode}
                  onChange={(e) => {
                    const recurring = e.target.value === 'recurring';
                    setValue('scheduled', recurring, { shouldValidate: true });
                    setValue('useRunDate', !recurring, {
                      shouldValidate: true,
                    });
                    if (!recurring && !watch('runDate')) {
                      setValue('runDate', nextHour().valueOf(), {
                        shouldValidate: true,
                      });
                    }
                  }}
                >
                  <FormControlLabel
                    value="recurring"
                    control={
                      <Radio
                        onClick={() => {
                          if (scheduleMode === 'recurring') {
                            // Deselecting the only active mode falls back to Manual Run.
                            if (!watch('manualRun')) {
                              setValue('manualRun', true, {
                                shouldValidate: true,
                              });
                            }
                            setValue('scheduled', false, {
                              shouldValidate: true,
                            });
                          }
                        }}
                      />
                    }
                    label="Recurring Schedule"
                  />
                  <FormHelperText sx={{ ml: 4, mt: -1 }}>
                    Set a repeating schedule — daily, weekly, or monthly.
                  </FormHelperText>
                  <FormControlLabel
                    value="runDate"
                    control={
                      <Radio
                        onClick={() => {
                          if (scheduleMode === 'runDate') {
                            // Deselecting the only active mode falls back to Manual Run.
                            if (!watch('manualRun')) {
                              setValue('manualRun', true, {
                                shouldValidate: true,
                              });
                            }
                            setValue('useRunDate', false, {
                              shouldValidate: true,
                            });
                          }
                        }}
                      />
                    }
                    label="Set Run Date"
                  />
                  <FormHelperText sx={{ ml: 4, mt: -1 }}>
                    Schedule this policy to run once at a specific date and
                    time.
                  </FormHelperText>
                </RadioGroup>
              </FormControl>

              <Box sx={{ width: 280, ml: 4 }}>
                <Controller
                  name="runDate"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      disabled={!useRunDate}
                      {...field}
                      value={field.value ? dayjs(field.value) : nextHour()}
                      label="Run Date"
                      views={['year', 'month', 'day', 'hours']}
                      onChange={(date: dayjs.Dayjs | null) => {
                        field.onChange(
                          date && date.isValid()
                            ? date.startOf('hour').valueOf()
                            : 0
                        );
                      }}
                      disablePast
                      slotProps={{
                        textField: {
                          error: !!errors.runDate,
                          helperText: errors.runDate?.message || ' ',
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </Stack>
          </Grid>
        ) : null}
      </Grid>
      <SectionHeader description="Optionally add any specific users/groups that you would like to send an email notification to upon this policies completion.">
        Email Notifications
      </SectionHeader>
      <Stack gap={1}>
        <Stack flexDirection="row" gap={1}>
          <Controller
            name={'emailsToNotify'}
            control={control}
            render={() => (
              <Box sx={{ width: 400 }}>
                <SharedAutocomplete
                  sources={['users', 'groups']}
                  autocompleteValue={autoCompleteResponse}
                  setAutoCompleteValue={(val) => {
                    setAutoCompleteResponse(val);
                    if (val && !emailsToNotify?.includes(val)) {
                      const updatedUsers = [...(emailsToNotify ?? []), val];
                      setValue('emailsToNotify', updatedUsers, {
                        shouldDirty: true,
                      });
                      autocompleteKey.current = new Date().getTime().toString();
                      setAutoCompleteResponse('');
                      setValue('notifyViaEmail', updatedUsers.length > 0);
                    }
                  }}
                />
              </Box>
            )}
          />
        </Stack>
        <Stack
          flexDirection="row"
          gap={1}
          flexWrap="wrap"
          sx={{ mt: 1, mb: 2 }}
        >
          {emailsToNotify && emailsToNotify.length > 0
            ? emailsToNotify.map((user) => {
                return (
                  <AvatarChip
                    key={user}
                    label={user}
                    type={'Unknown'}
                    email={user}
                    onDelete={() => {
                      const updatedEmails = emailsToNotify.filter(
                        (notifiedUser) => notifiedUser !== user
                      );
                      setValue('emailsToNotify', updatedEmails, {
                        shouldDirty: true,
                      });
                      setValue('notifyViaEmail', updatedEmails.length > 0);
                    }}
                  />
                );
              })
            : null}
        </Stack>
      </Stack>
    </Stack>
  );
}
